import { useState, useContext, createContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { baseUrl } from "../utilities/configurations";
import apiRequest from "../utilities/request";
import PropTypes from "prop-types";
import {
  UserManager,
  Config,
  FRUser,
  TokenManager,
} from "@forgerock/javascript-sdk";

Config.set({
  clientId: process.env.REACT_APP_WEB_OAUTH_CLIENT,
  redirectUri: `${process.env.REACT_APP_APP_URL}/callback`,
  scope: "openid profile email",
  serverConfig: {
    baseUrl: process.env.REACT_APP_AM_URL,
    timeout: 10000,
  },
  realmPath: process.env.REACT_APP_REALM_PATH,
});

const AuthContext = createContext();

const usePageVisibility = (onVisibilityChange) => {
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        onVisibilityChange();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [onVisibilityChange]);
};

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider(props) {

  const initMemberStatus = {
    isMember: false,
    isDependent: true, // initial disable of loa
    isPrepaid: false,
    isKycVerified: false,
    status: "" // status of api fetch
  }

  const [memberStatusLoadState, setMemberStatusLoadState] = useState("");
  const [memberStatus, setMemberStatus] = useState(initMemberStatus);
  const [isPhysiciansLoad, setIsPhysiciansLoad] = useState(false);
  const [isLocationDenied, setIsLocationDenied] = useState(true);
  const [showSplashscreen, setShowSplashscreen] = useState(true);
  const [authenticatedFrom, setAuthenticatedFrom] = useState("");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [policyLoadState, setPolicyLoadState] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [logoutReason, setLogoutReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [physicians, setPhysicians] = useState([]);
  const [currentPos, setCurrentPos] = useState();
  const [userData, setUserData] = useState([]);
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    checkAuth();
    getPhysicians();
    setTimeout(() => {
      setShowSplashscreen(false);
    }, 3000);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      oauthFlow();
    }
  }, [isAuthenticated]);

  const handleVisibilityChange = () => {
    if (isAuthenticated) {
      checkAuth();
    }
  };

  usePageVisibility(handleVisibilityChange);

  const getPhysicians = async () => {
    setIsPhysiciansLoad(true);
    const res = await import("../data/Physicians.json");
    setPhysicians(res.physicianList || []);
    setIsPhysiciansLoad(false);
  };

  const checkAuth = async () => {
    try {
      //check if there's a presence of token
      await TokenManager.getTokens();
      validateToken();
    } catch (error) {
      if(location?.pathname !== "/"){
        navigate("/",{replace: true});
      }
    }
  };

  const validateToken=async()=>{
    try {
      //check if tokens are valid
      await UserManager.getCurrentUser();
      setIsAuthenticated(true);
    } catch (err) {
      logout(true, "session expired");
    }
  }

  async function login() {
    try {
      await apiRequest(baseUrl + `/login`, "POST", {});
    } catch (error) {
      console.log(error);
    }
  }

  const getPolicy = async (state) => {
    try {
      setPolicyLoadState(state || "load");
      const res = await apiRequest(baseUrl + `/policy/policies`, "GET");
      if (!res.error) {
        setUserData(res.data || []);
        setPolicyLoadState("load success");
      } else if (res.status >= 500) {
        setPolicyLoadState("ISE");
      } else {
        setPolicyLoadState("load failed");
      }
    } catch (error) {
      setPolicyLoadState("timeout");
    }
  };

  const getMemberStatus = async () => {
    try {
      setMemberStatusLoadState("load");
      setMemberStatus(initMemberStatus);
      const res = await apiRequest(baseUrl + "/member/member-status", "GET");
      if (!res.error && res.data) {
        setMemberStatus({
          ...res.data,
          status: "success"
        });
        setMemberStatusLoadState("success");
      }else{
        setMemberStatusLoadState("failed");
      }
    } catch (error) {
      console.log(error);
      setMemberStatusLoadState("failed");
    }
  };

  async function oauthFlow() {
    try {
      const fr_tokens = await TokenManager.getTokens();
      const fr_user = await UserManager.getCurrentUser();
      if (fr_user) {
        login();
        getPolicy();
        getMemberStatus();
        setToken(fr_tokens);
        setUser(fr_user);
        if (authenticatedFrom === "login") {
          navigate("/home", { replace: true });
        }
      }
    } catch (err) {
      logout();
    }
  }

  const getCurrentPositionAsync = () => {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve(position.coords);
        },
        (error) => {
          reject(new Error(error));
        }
      );
    });
  };

  const fetchCurrentPosition = async () => {
    try {
      const position = await getCurrentPositionAsync();
      setIsLocationDenied(false);
      setCurrentPos(position);
    } catch (error) {
      console.log(error);
    }
  };

  const logout = async(showLogout, reason)=>{
    try {
      await FRUser.logout();
    } catch (error) {
      console.log(error)
    } finally {
      navigate('/');
      resetAuthValues();
      setShowLogoutModal(showLogout || false);
      setLogoutReason(reason || "");
    }
  }

  const resetAuthValues=()=>{
    setUserData([]);
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setMemberStatus(initMemberStatus);
    setAuthenticatedFrom("");
  }

  useEffect(() => {
    fetchCurrentPosition();
    let standalone = window.navigator.standalone,
      userAgent = window.navigator.userAgent.toLowerCase(),
      safari = /safari/.test(userAgent),
      ios = /iphone|ipod|ipad/.test(userAgent);
    if (ios) {
      if (!standalone && safari) {
        fetchCurrentPosition();
      } else if (standalone && !safari) {
        fetchCurrentPosition();
      } else if (!standalone && !safari) {
        //uiwebview
      }
    } else {
      fetchCurrentPosition();
    }
  }, []);

  const data = useMemo(
    () => ({
      setAuthenticatedFrom,
      setShowSplashscreen,
      setIsLocationDenied,
      setShowLogoutModal,
      setIsAuthenticated,
      setLogoutReason,
      getMemberStatus,
      setIsConnected,
      setCurrentPos,
      setIsLoading,
      setUserData,
      getPolicy,
      setToken,
      setUser,
      logout,

      memberStatusLoadState,
      authenticatedFrom,
      isPhysiciansLoad,
      isLocationDenied,
      showSplashscreen,
      showLogoutModal,
      isAuthenticated,
      policyLoadState,
      memberStatus,
      logoutReason,
      isConnected,
      currentPos,
      physicians,
      isLoading,
      userData,
      token,
      user,
    }),
    [
      memberStatusLoadState,
      authenticatedFrom,
      isPhysiciansLoad,
      isLocationDenied,
      showSplashscreen,
      showLogoutModal,
      isAuthenticated,
      policyLoadState,
      memberStatus,
      logoutReason,
      isConnected,
      physicians,
      currentPos,
      isLoading,
      userData,
      token,
      user,
    ]
  );

  return (
    <AuthContext.Provider value={data}>{props.children}</AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
